import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material'
import {
  Receipt as ReceiptIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Sync as SyncIcon,
} from '@mui/icons-material'
import { useSyncStatus } from '../../hooks/api/useSyncStatus'
import { useDashboardStats } from '../../hooks/api/useDashboard'
import { format } from 'date-fns'
import { formatCurrencyWithLocale } from '../../utils/formatters'
import StatusChip from '../../components/ui/StatusChip'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'

const Dashboard = () => {
  const user = useSelector((state: RootState) => state.auth.user)
  const uom = getCompanyUom(user?.company_uom)
  const { data: syncData, isLoading: syncLoading, error: syncError } = useSyncStatus()
  const { data: dashboardData, isLoading: dashboardLoading, error: dashboardError } = useDashboardStats()

  const stats = [
    {
      title: `Executed ${uom.plural}`,
      subtitle: dashboardData?.currentMonth.period.monthName || 'This Month',
      value: dashboardLoading ? '—' : (dashboardData?.currentMonth.totalExecutedCtns.toLocaleString() || '0'),
      icon: <ReceiptIcon sx={{ fontSize: 20 }} />,
    },
    {
      title: `Sales Value`,
      subtitle: dashboardData?.currentMonth.period.monthName || 'This Month',
      value: dashboardLoading ? '—' : `Rs ${formatCurrencyWithLocale(dashboardData?.currentMonth.totalSalesValue || 0)}`,
      icon: <AssignmentIcon sx={{ fontSize: 20 }} />,
    },
    {
      title: 'Customers',
      subtitle: 'Total registered',
      value: dashboardLoading ? '—' : (dashboardData?.systemTotals.totalCustomers.toLocaleString() || '0'),
      icon: <PeopleIcon sx={{ fontSize: 20 }} />,
    },
    {
      title: 'Total Outstanding Balance',
      subtitle: 'Across customers',
      value: dashboardLoading ? '—' : `Rs ${formatCurrencyWithLocale(dashboardData?.systemTotals.totalOutstandingBalance || 0)}`,
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />,
    },
  ]

  return (
    <Box>
      <Stack spacing={3}>
        {dashboardError && <Alert severity="error">Failed to load dashboard statistics. Please try again.</Alert>}
        {/* KPI Tiles */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
          {stats.map((stat) => (
            <Card key={stat.title}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: '#7a766f',
                    }}
                  >
                    {stat.title}
                  </Typography>
                  <Box sx={{ color: '#2d6a4f', opacity: 0.7 }}>{stat.icon}</Box>
                </Box>
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#2c2a26', lineHeight: 1, mb: 0.5 }}>
                  {stat.value}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#7a766f' }}>{stat.subtitle}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Booker Sync Status */}
        <Paper>
          <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #e8e4dd' }}>
            <SyncIcon sx={{ fontSize: 18, color: '#2d6a4f' }} />
            <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#2c2a26' }}>
              Booker Sync Status
            </Typography>
            {syncData && (
              <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
                <Typography sx={{ fontSize: '0.8125rem', color: '#7a766f' }}>
                  Total <strong style={{ color: '#2c2a26' }}>{syncData.data.summary.total}</strong>
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: '#7a766f' }}>
                  Synced <strong style={{ color: '#2d6a4f' }}>{syncData.data.summary.synced}</strong>
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: '#7a766f' }}>
                  Pending <strong style={{ color: '#7a5a20' }}>{syncData.data.summary.pending}</strong>
                </Typography>
              </Box>
            )}
          </Box>

          {syncLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={28} />
            </Box>
          ) : syncError ? (
            <Box p={2}>
              <Alert severity="error">Failed to load sync status. Please try again.</Alert>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Booker Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sync</TableCell>
                    <TableCell>Last Sync</TableCell>
                    <TableCell align="right">Daily Bookings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncData?.data.bookers.map((booker) => (
                    <TableRow key={booker.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{booker.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{booker.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          label={booker.status}
                          variant={booker.status === 'active' ? 'success' : 'neutral'}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          label={booker.is_synced ? 'Synced' : 'Pending'}
                          variant={booker.is_synced ? 'success' : 'warn'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {booker.last_sync_date
                            ? format(new Date(booker.last_sync_date), 'MMM dd, yyyy h:mm a')
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {booker.daily_booking_count || 0}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>
    </Box>
  )
}

export default Dashboard
